export const BLOCK_ACTION_KINDS = [
  "navigate",
  "submit_form",
  "book_service",
  "open_chat",
  "register_webinar",
  "claim_offer",
] as const;

export type BlockActionKind = (typeof BLOCK_ACTION_KINDS)[number];
export type BlockActionTarget = "same_tab" | "new_tab";

interface BaseBlockAction {
  kind: BlockActionKind;
  target?: BlockActionTarget;
}

export interface NavigateBlockAction extends BaseBlockAction {
  kind: "navigate";
  url: string;
}

export interface SubmitFormBlockAction extends BaseBlockAction {
  kind: "submit_form";
  formId?: string;
}

export interface BookServiceBlockAction extends BaseBlockAction {
  kind: "book_service";
  serviceId?: string;
  serviceSlug?: string;
}

export interface OpenChatBlockAction extends BaseBlockAction {
  kind: "open_chat";
  conversationId?: string;
  webinarId?: string;
}

export interface RegisterWebinarBlockAction extends BaseBlockAction {
  kind: "register_webinar";
  webinarId?: string;
  webinarSlug?: string;
  occurrenceId?: string;
}

export interface ClaimOfferBlockAction extends BaseBlockAction {
  kind: "claim_offer";
  offerId?: string;
  offerSlug?: string;
}

export type BlockAction =
  | NavigateBlockAction
  | SubmitFormBlockAction
  | BookServiceBlockAction
  | OpenChatBlockAction
  | RegisterWebinarBlockAction
  | ClaimOfferBlockAction;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function asNonEmptyString(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function normalizeTarget(value: unknown): BlockActionTarget | undefined {
  return value === "new_tab" || value === "same_tab" ? value : undefined;
}

function encodePathSegment(value: string): string {
  return encodeURIComponent(value.trim());
}

function buildQuery(params: Record<string, string | undefined>): string {
  const query = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value) {
      query.set(key, value);
    }
  }

  const serialized = query.toString();
  return serialized ? `?${serialized}` : "";
}

export function normalizeBlockAction(action: unknown): BlockAction | null {
  if (!isRecord(action)) {
    return null;
  }

  const kind = asNonEmptyString(action.kind);
  const target = normalizeTarget(action.target);

  switch (kind) {
    case "navigate": {
      const url = asNonEmptyString(action.url);
      return url ? { kind, url, target } : null;
    }
    case "submit_form":
      return {
        kind,
        target,
        formId: asNonEmptyString(action.formId),
      };
    case "book_service":
      return {
        kind,
        target,
        serviceId: asNonEmptyString(action.serviceId),
        serviceSlug: asNonEmptyString(action.serviceSlug),
      };
    case "open_chat":
      return {
        kind,
        target,
        conversationId: asNonEmptyString(action.conversationId),
        webinarId: asNonEmptyString(action.webinarId),
      };
    case "register_webinar":
      return {
        kind,
        target,
        webinarId: asNonEmptyString(action.webinarId),
        webinarSlug: asNonEmptyString(action.webinarSlug),
        occurrenceId: asNonEmptyString(action.occurrenceId),
      };
    case "claim_offer":
      return {
        kind,
        target,
        offerId: asNonEmptyString(action.offerId),
        offerSlug: asNonEmptyString(action.offerSlug),
      };
    default:
      return null;
  }
}

export function buildBlockActionHref(action: BlockAction | null | undefined): string | null {
  if (!action) {
    return null;
  }

  switch (action.kind) {
    case "navigate":
      return action.url;
    case "submit_form":
      return action.formId ? `#${encodeURIComponent(action.formId)}` : "#form";
    case "book_service":
      return `/services/bookings${buildQuery({
        serviceId: action.serviceId,
        serviceSlug: action.serviceSlug,
      })}`;
    case "open_chat":
      if (action.webinarId) {
        return `/webinars/${encodePathSegment(action.webinarId)}`;
      }
      return null;
    case "register_webinar": {
      const identifier = action.webinarSlug ?? action.webinarId;
      const path = identifier ? `/webinars/${encodePathSegment(identifier)}` : "/webinars";
      return `${path}${buildQuery({ occurrenceId: action.occurrenceId })}`;
    }
    case "claim_offer": {
      const identifier = action.offerSlug ?? action.offerId;
      return identifier ? `/offers/${encodePathSegment(identifier)}` : "/offers";
    }
  }
}
